import React from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

type Props = {
  disabled?: boolean
  reason: string | undefined
  id: string
  placement?: 'top' | 'right' | 'bottom' | 'left'
  // Use inline display when wrapping a link or span inside a paragraph,
  // so the tooltip target doesn't force a new line. Defaults to block
  // (the existing behavior, used by nav links).
  inline?: boolean
  // Opt out of the default `role="note"` + `aria-label={reason}` wrapper,
  // which makes the reason ALONE the wrapper's accessible name — fine when
  // the child has no name of its own (a bare nav link), but it overrides a
  // child that already carries one (e.g. a disabled tab's title), leaving a
  // screen-reader user with no indication of WHICH control is locked (WCAG
  // 2.1 4.1.2). When true, the wrapper carries no role/aria-label — the
  // child's own content supplies the accessible name — and the reason is
  // exposed via `aria-describedby` pointing at a visually-hidden span
  // instead, so it's still reachable by keyboard/AT, just not as the name.
  // Defaults to false so every existing caller (nav links, action buttons)
  // is unaffected.
  srOnlyReason?: boolean
  children: React.ReactNode
}

export const PermissionTooltip = ({
  disabled,
  reason,
  id,
  placement = 'right',
  inline = false,
  srOnlyReason = false,
  children,
}: Props) => {
  if (!disabled || !reason) return <>{children}</>

  const descriptionId = `${id}-description`

  return (
    <OverlayTrigger
      placement={placement}
      trigger={['hover', 'focus']}
      overlay={<Tooltip id={id}>{reason}</Tooltip>}
    >
      <span
        className={inline ? 'd-inline' : 'd-block w-100'}
        tabIndex={0}
        role={srOnlyReason ? undefined : 'note'}
        aria-label={srOnlyReason ? undefined : reason}
        aria-describedby={srOnlyReason ? descriptionId : undefined}
      >
        {children}
        {srOnlyReason && (
          <span id={descriptionId} className="sr-only">
            {reason}
          </span>
        )}
      </span>
    </OverlayTrigger>
  )
}
